import { useConfirm } from "@/contexts/ConfirmDialogContext";
import { api } from "@/lib/api";
import {
  Badge,
  Button,
  Input,
  Label,
  Select,
  Switch,
  SwitchField,
  TableCell,
  TableRow,
  Textarea,
} from "@a7y/tailwind-catalyst";
import type {
  ReleaseNoteCategory,
  ReleaseNoteItem,
} from "@a7y/types";
import * as Headless from "@headlessui/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ChevronDown, ChevronUp, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import {
  CATEGORY_BADGE_COLOR,
  CATEGORY_LABEL,
  CATEGORY_OPTIONS,
} from "./constants";

/**
 * One inline-editable row inside a release-note entry card.
 *
 * Modes:
 *   - view (existing item, not yet clicked Edit)
 *   - edit (existing item in edit mode)
 *   - draft (brand-new item, mounts directly in edit mode; cancelling removes
 *     the row via onCancelDraft)
 *
 * Mutations invalidate the `["release-notes", startDate, endDate]` and
 * `["release-note-weeks"]` queries so the weeks rail counts stay in sync.
 */

interface ReleaseNoteItemRowProps {
  entryId: string;
  index: number;
  item: ReleaseNoteItem;
  itemCount: number;
  isDraft?: boolean;
  isBusy: boolean;
  onBusyChange: (busy: boolean) => void;
  onCancelDraft?: () => void;
  queryKey: readonly unknown[];
}

export function ReleaseNoteItemRow({
  entryId,
  index,
  item,
  itemCount,
  isDraft = false,
  isBusy,
  onBusyChange,
  onCancelDraft,
  queryKey,
}: ReleaseNoteItemRowProps) {
  const queryClient = useQueryClient();
  const confirm = useConfirm();

  const [isEditing, setIsEditing] = useState<boolean>(isDraft);
  const [category, setCategory] = useState<ReleaseNoteCategory>(item.category);
  const [userFacing, setUserFacing] = useState<boolean>(item.userFacing);
  const [title, setTitle] = useState<string>(item.title);
  const [userFacingTitle, setUserFacingTitle] = useState<string>(
    item.userFacingTitle ?? "",
  );
  const [description, setDescription] = useState<string>(item.description ?? "");

  // Sync local edit state when the underlying item changes (e.g. after reorder
  // or sibling mutation returns a new array). Skip while actively editing so
  // we don't stomp the user's in-progress form.
  useEffect(() => {
    if (isEditing) return;
    // WeekDetail.tsx:132 renders these rows from entry.items.map with
    // key={index}, so a reorder deliberately reuses the same component instance
    // for a different item — that reuse is the whole reason this effect exists.
    // Dropping it would leave every reordered row showing the previous item's
    // fields, and the isEditing guard above is what keeps an in-progress edit
    // from being stomped.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setCategory(item.category);
    setUserFacing(item.userFacing);
    setTitle(item.title);
    setUserFacingTitle(item.userFacingTitle ?? "");
    setDescription(item.description ?? "");
  }, [isEditing, item]);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey });
    queryClient.invalidateQueries({ queryKey: ["release-note-weeks"] });
  };

  const updateMutation = useMutation({
    mutationFn: () =>
      api.updateReleaseNoteItem(entryId, index, {
        category,
        userFacing,
        userFacingTitle: userFacingTitle.trim() || undefined,
        description: description.trim() || undefined,
      }),
    onSuccess: () => {
      invalidate();
      setIsEditing(false);
    },
  });

  const createMutation = useMutation({
    mutationFn: () =>
      api.createReleaseNoteItem(entryId, {
        category,
        title: title.trim(),
        userFacing,
        userFacingTitle: userFacingTitle.trim() || undefined,
        description: description.trim() || undefined,
      }),
    onSuccess: () => {
      invalidate();
      onCancelDraft?.();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => api.deleteReleaseNoteItem(entryId, index),
    onSuccess: invalidate,
  });

  const reorderMutation = useMutation({
    mutationFn: (order: number[]) =>
      api.reorderReleaseNoteItems(entryId, order),
    onSuccess: invalidate,
  });

  // Report transient busy state up to the card so siblings disable during a
  // mutation. This avoids two admins reordering a row twice in a race.
  const selfBusy =
    updateMutation.isPending ||
    createMutation.isPending ||
    deleteMutation.isPending ||
    reorderMutation.isPending;
  useEffect(() => {
    onBusyChange(selfBusy);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selfBusy]);

  const handleCancel = () => {
    if (isDraft) {
      onCancelDraft?.();
      return;
    }
    setCategory(item.category);
    setUserFacing(item.userFacing);
    setUserFacingTitle(item.userFacingTitle ?? "");
    setDescription(item.description ?? "");
    setIsEditing(false);
  };

  const handleSave = () => {
    if (isDraft) {
      if (!title.trim()) return;
      createMutation.mutate();
    } else {
      updateMutation.mutate();
    }
  };

  const handleDelete = async () => {
    const ok = await confirm({
      title: "Delete release-note item?",
      description: (
        <span>
          This will permanently remove <strong>{item.title}</strong> from this
          release. This cannot be undone.
        </span>
      ),
      confirmLabel: "Delete",
      confirmColor: "red",
    });
    if (!ok) return;
    deleteMutation.mutate();
  };

  const handleMove = (direction: "up" | "down") => {
    const order = Array.from({ length: itemCount }, (_, i) => i);
    const target = direction === "up" ? index - 1 : index + 1;
    if (target < 0 || target >= itemCount) return;
    [order[index], order[target]] = [order[target], order[index]];
    reorderMutation.mutate(order);
  };

  const mutationError =
    updateMutation.error ??
    createMutation.error ??
    deleteMutation.error ??
    reorderMutation.error;

  // ---- Render: edit / draft mode --------------------------------------------
  if (isEditing) {
    const saveDisabled =
      selfBusy || (isDraft && title.trim().length === 0);
    return (
      <TableRow>
        <TableCell colSpan={5} className="py-4 align-top">
          <div className="space-y-4">
            {isDraft && (
              <Headless.Field>
                <Label>Internal title (commit-style)</Label>
                <Input
                  className="mt-1.5"
                  value={title}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setTitle(e.target.value)
                  }
                  placeholder="A7Y-XXX: short imperative subject"
                  required
                  autoFocus
                />
              </Headless.Field>
            )}

            <div className="grid gap-4 sm:grid-cols-2">
              <Headless.Field>
                <Label>Category</Label>
                <Select
                  className="mt-1.5"
                  value={category}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                    setCategory(e.target.value as ReleaseNoteCategory)
                  }
                >
                  {CATEGORY_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </Select>
              </Headless.Field>

              <SwitchField>
                <Label>User-facing (shown on public page)</Label>
                <Switch
                  checked={userFacing}
                  onChange={(checked: boolean) => setUserFacing(checked)}
                  color="blue"
                />
              </SwitchField>
            </div>

            <Headless.Field>
              <Label>User-facing title</Label>
              <Input
                className="mt-1.5"
                value={userFacingTitle}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setUserFacingTitle(e.target.value)
                }
                placeholder="Clean title shown on the public release-notes page"
              />
            </Headless.Field>

            <Headless.Field>
              <Label>Description</Label>
              <Textarea
                className="mt-1.5"
                rows={3}
                value={description}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                  setDescription(e.target.value)
                }
                placeholder="Short description shown under the title"
              />
            </Headless.Field>

            {mutationError && (
              <div className="rounded-lg bg-red-50 p-3 text-sm text-red-800 dark:bg-red-900/20 dark:text-red-400">
                {mutationError.message || "Failed to save item"}
              </div>
            )}

            <div className="flex items-center gap-2 pt-1">
              <Button color="blue" onClick={handleSave} disabled={saveDisabled}>
                {createMutation.isPending || updateMutation.isPending
                  ? "Saving..."
                  : isDraft
                    ? "Add Item"
                    : "Save"}
              </Button>
              <Button plain onClick={handleCancel} disabled={selfBusy}>
                Cancel
              </Button>
            </div>
          </div>
        </TableCell>
      </TableRow>
    );
  }

  // ---- Render: view mode ----------------------------------------------------
  const disableActions = isBusy;
  return (
    <TableRow>
      <TableCell className="align-top">
        <Badge color={CATEGORY_BADGE_COLOR[item.category]}>
          {CATEGORY_LABEL[item.category]}
        </Badge>
      </TableCell>
      <TableCell className="align-top">
        <div className="space-y-0.5">
          {item.userFacingTitle ? (
            <>
              <p className="font-medium text-zinc-900 dark:text-white">
                {item.userFacingTitle}
              </p>
              <p className="text-xs text-zinc-500 dark:text-zinc-400">
                {item.title}
              </p>
            </>
          ) : (
            <p className="font-medium text-zinc-900 dark:text-white">
              {item.title}
            </p>
          )}
        </div>
      </TableCell>
      <TableCell className="align-top text-sm text-zinc-600 dark:text-zinc-300">
        {item.description ? (
          <span>{item.description}</span>
        ) : (
          <span className="text-zinc-400 dark:text-zinc-500">
            &mdash;
          </span>
        )}
      </TableCell>
      <TableCell className="align-top">
        {item.userFacing ? (
          <Badge color="blue">Public</Badge>
        ) : (
          <Badge color="zinc">Internal</Badge>
        )}
      </TableCell>
      <TableCell className="align-top">
        <div className="flex items-center justify-end gap-1">
          <button
            type="button"
            aria-label="Move up"
            onClick={() => handleMove("up")}
            disabled={disableActions || index === 0}
            className="rounded p-1 text-zinc-500 hover:bg-zinc-100 disabled:cursor-not-allowed disabled:opacity-30 dark:text-zinc-400 dark:hover:bg-zinc-800"
          >
            <ChevronUp className="h-4 w-4" />
          </button>
          <button
            type="button"
            aria-label="Move down"
            onClick={() => handleMove("down")}
            disabled={disableActions || index === itemCount - 1}
            className="rounded p-1 text-zinc-500 hover:bg-zinc-100 disabled:cursor-not-allowed disabled:opacity-30 dark:text-zinc-400 dark:hover:bg-zinc-800"
          >
            <ChevronDown className="h-4 w-4" />
          </button>
          <Button
            plain
            onClick={() => setIsEditing(true)}
            disabled={disableActions}
          >
            Edit
          </Button>
          <button
            type="button"
            aria-label="Delete"
            onClick={handleDelete}
            disabled={disableActions}
            className="rounded p-1 text-red-500 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-30 dark:hover:bg-red-900/20"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </TableCell>
    </TableRow>
  );
}
